import React from "react";
import { styled } from "@mui/material/styles";
import { Typography, CardContent, Card, Box, Link as A } from "@mui/material";
import { Link } from "react-router-dom";

const PREFIX = "MediaCard";

const classes = {
  root: `${PREFIX}-root`,
  media: `${PREFIX}-media`,
  link: `${PREFIX}-link`,
  content: `${PREFIX}-content`,
  description: `${PREFIX}-description`,
  title: `${PREFIX}-title`,
};

const StyledBox = styled(Box)(() => ({
  [`& .${classes.root}`]: {
    "width": "100%",
    "height": 180,
    "@media (max-width:820px)": {
      height: 240,
    },

    "boxShadow": "none",
    "overflow": "visible",
    "padding": 0,
  },

  [`& .${classes.media}`]: {
    position: "relative",
    width: "100%",
    height: "100%",
  },

  "& .default-img": {
    position: "absolute",
    top: 0,
    left: 0,
    width: 240,
    height: 160,
    objectFit: "cover",
    borderRadius: 3,
    transition: "opacity .3s ease",
    opacity: 1,
  },

  "& .hover-img": {
    position: "absolute",
    top: 0,
    left: 0,
    width: 240,
    height: 160,
    objectFit: "cover",
    borderRadius: 3,
    transition: "opacity .3s ease",
    opacity: 0,
  },
  [`& .${classes.link}:hover .hover-img`]: {
    opacity: 1,
  },
  [`& .${classes.link}:hover .default-img`]: {
    opacity: 0,
  },

  [`& .${classes.link}`]: {
    "display": "block",
    "width": "100%",
    "height": "100%",
    "position": "relative",
    "borderRadius": 3,
    "transition": ".3s ease",
    "@media (max-width:600px)": {
      cursor: "default",
    },
    "& img": {
      borderRadius: 3,
      transition: ".3s ease",
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
  },

  [`& .${classes.content}`]: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
  },

  [`& .${classes.description}`]: {
    fontSize: "16px",
    textAlign: "center",
    paddingTop: 0,
    paddingLeft: 8,
    paddingRight: 8,
  },

  [`& .${classes.title}`]: {
    fontFamily: "Source Sans Pro, sans-serif",
    fontWeight: 700,
    fontSize: "1.125rem",
    marginBottom: 0,
    textAlign: "center",
    paddingBottom: 0,
    paddingTop: "8px",
  },
}));

export const MediaCard = ({
  img,
  hoverImg,
  hoveredCard,
  title,
  description,
  href,
  useHref,
  id,
  showBeta,
  imageHeight,
  setHoveredCard,
  moreInfo,
}: {
  img: any;
  hoverImg?: string;
  inactiveImg?: string;
  hoveredCard?: string | null;
  setHoveredCard?: (id: string | undefined | null) => void;
  title?: string;
  description?: string;
  href: string;
  useHref?: boolean;
  id?: string;
  showBeta?: boolean;
  imageHeight?: number;
  moreInfo?: React.ReactNode;
}) => {
  const titleComponent = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography
        className={classes.title}
        gutterBottom
        variant="h3"
        id={id}
        onMouseEnter={() => setHoveredCard?.(id)}
        onFocus={() => setHoveredCard?.(id)}
        onMouseLeave={() => setHoveredCard?.(null)}
        onBlur={() => setHoveredCard?.(null)}
      >
        {title}{" "}
        {showBeta ? (
          <>
            <span> &ndash; </span>
            <span
              style={{
                color: "#AA0033",
              }}
            >
              BETA
            </span>
          </>
        ) : undefined}
      </Typography>
      {moreInfo}
    </Box>
  );
  const isHovered = hoveredCard === id;
  const isAnotherHovered = hoveredCard && hoveredCard !== id;

  const shownImage = isHovered ? hoverImg : isAnotherHovered ? img : img;
  const cardComponent = (
    <Box className={classes.media}>
      <img
        src={shownImage}
        alt={title}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
        }}
      />
    </Box>
  );
  return (
    <StyledBox>
      <Card
        className={classes.root}
        onMouseEnter={() => setHoveredCard?.(id)}
        onFocus={() => setHoveredCard?.(id)}
        onMouseLeave={() => setHoveredCard?.(null)}
        onBlur={() => setHoveredCard?.(null)}
      >
        {useHref ? (
          <A href={href} id={`${id}-card`} className={classes.link}>
            {cardComponent}
          </A>
        ) : (
          <Link to={href} id={`${id}-card`} className={classes.link}>
            {cardComponent}
          </Link>
        )}
      </Card>
      {titleComponent}
      <CardContent className={classes.content}>
        <Typography className={classes.description} component="p">
          {description}
        </Typography>
      </CardContent>
    </StyledBox>
  );
};
